'use client';

import {
    lazy,
    Suspense,
    type ChangeEvent,
    type FormEvent,
    type ReactElement,
    useCallback,
    useRef,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@/hooks/use-navigate';
import { FormInputField } from '@/shared-ui/molecules';
import { UITarget } from '@/targets/ui-target';

const SearchController = lazy(() => import('./search-controller'));

export default function Search(): ReactElement {
    const { t } = useTranslation('search');
    const navigate = useNavigate();
    const containerRef = useRef<HTMLDivElement | null>(null);
    const searchAnchorRef = useRef<HTMLDivElement | null>(null);
    const [query, setQuery] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [controllerLoaded, setControllerLoaded] = useState(false);

    const openSuggestionsPanel = useCallback(() => {
        setControllerLoaded(true);
        setShowSuggestions(true);
    }, []);

    const handleInputChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            setQuery(e.target.value);
            openSuggestionsPanel();
        },
        [openSuggestionsPanel]
    );

    const handleSubmit = useCallback(
        (e: FormEvent) => {
            e.preventDefault();
            const searchQuery = query.trim();
            if (searchQuery) {
                setShowSuggestions(false);
                void navigate(`/search?q=${encodeURIComponent(searchQuery)}`, {
                    state: { query: searchQuery },
                });
            }
        },
        [navigate, query]
    );

    return (
        <div ref={containerRef} className="relative">
            <form onSubmit={handleSubmit} className="relative z-50">
                <div className="relative">
                    <UITarget targetId="sfcc.header.search.input">
                        <FormInputField.Root>
                            <FormInputField.InputWrapper ref={searchAnchorRef} actionType={'button'}>
                                <FormInputField.Input
                                    onChange={handleInputChange}
                                    onFocus={openSuggestionsPanel}
                                    aria-label={t('placeholder')}
                                    aria-autocomplete="list"
                                    aria-expanded={showSuggestions}
                                    aria-haspopup="listbox"
                                    role="combobox"
                                    data-testid="header-search"
                                    value={query}
                                    placeholder={t('placeholder')}
                                />
                                <FormInputField.Button type="submit">{t('submit')}</FormInputField.Button>
                            </FormInputField.InputWrapper>
                        </FormInputField.Root>
                    </UITarget>
                </div>
            </form>
            {controllerLoaded ? (
                <Suspense fallback={null}>
                    <SearchController
                        query={query}
                        showSuggestions={showSuggestions}
                        containerRef={containerRef}
                        anchorRef={searchAnchorRef}
                        closeSuggestions={() => setShowSuggestions(false)}
                    />
                </Suspense>
            ) : null}
        </div>
    );
}
